// useGameOrientationLock — enforce a game's required orientation.
//
// ════════════════════════════════════════════════════════════════════
// WHY THIS EXISTS
// ════════════════════════════════════════════════════════════════════
//
// Each game declares an `orientation` on its GameModule (see
// player-frontend/src/games/types.ts). Some games — Ludo, Chess, XO,
// Carrom — render pixel-perfectly only in PORTRAIT and look broken on
// landscape phones. Rummy is the inverse: its multi-seat table only
// fits in LANDSCAPE. The shell needs ONE place that takes the declared
// orientation and:
//
//   1. Attempts a hardware lock so a modern Android Chrome session
//      refuses to rotate away from the required orientation.
//   2. Reactively reports whether the device CURRENTLY matches the
//      requirement, so the caller can render either a CSS-rotation
//      wrapper (portrait games) or a "please rotate" overlay
//      (landscape games) on platforms where the hardware lock
//      silently fails (iOS Safari, browser without PWA fullscreen).
//   3. Reports the exact `screen.orientation.angle` so the caller's
//      CSS wrapper can apply the correct *counter*-rotation. A
//      single `rotate(90deg)` is right for one of the two landscape
//      orientations but produces a 180° flip for the other; the
//      angle field lets the caller pick the matching transform.
//   4. Cleans up the lock on unmount so navigating away from the
//      game restores the normal "any orientation" behaviour for the
//      dashboard / wallet / profile pages.
//
// ════════════════════════════════════════════════════════════════════
// CAVEATS
// ════════════════════════════════════════════════════════════════════
//
// • Hardware orientation lock requires PWA fullscreen or an explicit
//   user gesture on most browsers. It's BEST-EFFORT — the CSS
//   wrapper/overlay the caller renders is the authoritative defence.
//
// • The hook attaches a media-query listener to (orientation: portrait)
//   AND subscribes to screen.orientation `change` events. Modern
//   browsers fire both reliably; we also re-read once on `pageshow`
//   (back/forward cache restore) so a user who locked their phone in
//   landscape, switched apps, and came back doesn't see a stale state.

import { useEffect, useState } from 'react';

// Re-defined inline (rather than imported from player-frontend) so
// shared-ui has no upstream dependency on the player package. The
// player-frontend's GameModule type's GameOrientation is the SAME
// string literal union — they're structurally identical and must
// stay in sync.
export type GameOrientation = 'portrait' | 'landscape' | 'any';

/** Discrete device-orientation angle. Anything else (15° tilts etc.)
 *  is snapped to the nearest multiple of 90 by `readAngle`. */
export type ScreenAngle = 0 | 90 | 180 | 270;

export type OrientationLockStatus = {
  /** What the device looks like right now. */
  current: 'portrait' | 'landscape';
  /** Screen-rotation angle reported by the OS. Refines `current` so
   *  callers can tell portrait-secondary (upside-down portrait, 180°)
   *  apart from normal portrait (0°), and landscape-primary (90°)
   *  apart from landscape-secondary (270°). The CSS-rotation
   *  wrapper uses this to pick the right counter-rotation. */
  angle: ScreenAngle;
  /** What the game declared it needs. */
  required: GameOrientation;
  /** True when the device's current angle doesn't match the game's
   *  required orientation:
   *    • required='portrait'  → mismatch when angle ≠ 0
   *    • required='landscape' → mismatch when angle ≠ 90 and ≠ 270
   *    • required='any'       → always false
   *  Caller renders a counter-rotation wrapper (portrait) or a
   *  rotate-prompt overlay (landscape) on this. */
  mismatch: boolean;
};

function readOrientation(): 'portrait' | 'landscape' {
  if (typeof window === 'undefined') return 'portrait';
  return window.matchMedia('(orientation: portrait)').matches ? 'portrait' : 'landscape';
}

function readAngle(): ScreenAngle {
  if (typeof window === 'undefined') return 0;
  // `screen.orientation.angle` is the canonical source. Some older
  // browsers expose `window.orientation` (deprecated but widely
  // shipped) — fall back to it when the modern API is missing.
  const so = (screen as unknown as { orientation?: { angle?: number } }).orientation;
  let raw: number | undefined = so?.angle;
  if (raw == null) raw = (window as unknown as { orientation?: number }).orientation;
  if (raw == null) raw = 0;
  // Normalise: window.orientation can return -90; the spec angle is
  // always non-negative. Wrap into [0, 360) and snap to the nearest
  // multiple of 90 so the consumer can rely on the four-value union.
  const wrapped = ((Number(raw) % 360) + 360) % 360;
  const snapped = (Math.round(wrapped / 90) * 90) % 360;
  return (snapped as ScreenAngle);
}

/**
 * Subscribe to orientation changes and (best-effort) lock the device
 * to the game's required orientation. Returns the current status the
 * caller uses to decide whether to render a counter-rotation wrapper
 * or a rotate-prompt overlay.
 *
 * Pass `'any'` (or omit) to disable enforcement entirely — useful
 * for games that work fine in either orientation.
 */
export function useGameOrientationLock(required: GameOrientation = 'any'): OrientationLockStatus {
  const [current, setCurrent] = useState<'portrait' | 'landscape'>(readOrientation);
  const [angle, setAngle] = useState<ScreenAngle>(readAngle);

  useEffect(() => {
    if (required === 'any') return;

    // Attempt the native hardware lock. Throws on browsers that don't
    // support it OR if we're not in PWA fullscreen — wrap in try so
    // the failure is silent and we fall through to the CSS overlay.
    try {
      const so = (screen as unknown as { orientation?: { lock?: (o: string) => Promise<void> } }).orientation;
      so?.lock?.(required)?.catch?.(() => { /* swallow */ });
    } catch { /* not supported */ }

    const mq = window.matchMedia('(orientation: portrait)');
    const refresh = () => { setCurrent(readOrientation()); setAngle(readAngle()); };
    mq.addEventListener?.('change', refresh);

    // screen.orientation fires a 'change' event for angle transitions
    // (e.g. landscape-primary → landscape-secondary at the same axis).
    // That doesn't show up in the (orientation: portrait) MQ, so this
    // listener is what catches a fresh 180° flip.
    const so = (screen as unknown as { orientation?: EventTarget & { angle?: number } }).orientation;
    so?.addEventListener?.('change', refresh);

    const onPageshow = () => refresh();
    window.addEventListener('pageshow', onPageshow);

    return () => {
      mq.removeEventListener?.('change', refresh);
      so?.removeEventListener?.('change', refresh);
      window.removeEventListener('pageshow', onPageshow);
      // Release the hardware lock so the next route (dashboard,
      // wallet, etc.) gets normal orientation behaviour back.
      try {
        const u = (screen as unknown as { orientation?: { unlock?: () => void } }).orientation;
        u?.unlock?.();
      } catch { /* ignore */ }
    };
  }, [required]);

  // Mismatch is angle-precise: portrait-secondary (180°) counts as a
  // mismatch under 'portrait' even though the MQ says we're still in
  // portrait — the user wants the game locked at angle=0, period.
  let mismatch = false;
  if (required === 'portrait')  mismatch = angle !== 0;
  if (required === 'landscape') mismatch = angle !== 90 && angle !== 270;

  return { current, angle, required, mismatch };
}
